import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, User, Bell, Globe, Shield, Loader2, Camera, Check, Eye, EyeOff, Download, Mail, MessageCircle, Upload, FileText, FileImage, FileSpreadsheet, Sparkles, Trash2, Settings2, RefreshCw, Wrench, Archive, FileArchive, CreditCard, Crown, Zap, Building2, ExternalLink, Link2, Cookie, X, AlertTriangle, ChevronDown } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n/useTranslation";
import type { Language } from "@/lib/i18n/translations";
import type { Tables } from "@/integrations/supabase/types";
import TeamManagement from "@/components/team/TeamManagement";
import { PushNotificationToggle } from "@/components/notifications/PushNotificationToggle";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { UpgradePrompt } from "@/components/usage/UpgradePrompt";
import { useGmailConnection } from "@/hooks/useGmailConnection";
import { useEasySyncConnection } from "@/hooks/useEasySyncConnection";
import { GmailSyncProgress } from "@/components/gmail/GmailSyncProgress";
import { ReferralSection } from "@/components/referral/ReferralSection";
import { SEO } from "@/components/seo/SEO";
import { OfficialDocumentsCard } from "@/components/settings/OfficialDocumentsCard";
import { SettingsTabNav, type SettingsTab } from "@/components/settings/SettingsTabNav";
import { ConnectedSourcesCard } from "@/components/settings/ConnectedSourcesCard";
import { BiometricSettings } from "@/components/settings/BiometricSettings";
import { useIsMobile } from "@/hooks/use-mobile";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
type ImportSetting = Tables<"import_settings">;

// Local persistence for semantic indexing so it can resume after a refresh
const LS_EMBEDDING_INDEXING_ACTIVE = "ef:embeddingIndexingActive";
const LS_EMBEDDING_INDEXING_TOTAL = "ef:embeddingIndexingTotal";

// Gmail Import Configuration component - shown before first sync
const GmailImportConfig = ({ 
  userId, 
  onConfigured, 
  onSync 
}: { 
  userId: string | undefined; 
  onConfigured: (setting: ImportSetting) => void;
  onSync: () => Promise<void>;
}) => {
  const [periodMonths, setPeriodMonths] = useState("12");
  const [maxSizeMb, setMaxSizeMb] = useState("10");
  const [fileTypes, setFileTypes] = useState(["pdf", "images", "office"]);
  const [attachmentsOnly, setAttachmentsOnly] = useState(true);
  const [excludeNewsletters, setExcludeNewsletters] = useState(true);
  const [smartImport, setSmartImport] = useState(true);
  const [saving, setSaving] = useState(false);

  const toggleFileType = (type: string) => {
    setFileTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleSaveAndSync = async () => {
    if (!userId || fileTypes.length === 0) {
      toast.error("Veuillez sélectionner au moins un type de fichier");
      return;
    }

    setSaving(true);
    
    // Create import_settings entry
    const { data, error } = await supabase
      .from("import_settings")
      .insert({
        user_id: userId,
        provider: "gmail" as const,
        period_months: parseInt(periodMonths),
        max_size_mb: parseInt(maxSizeMb),
        file_types: fileTypes,
        attachments_only: attachmentsOnly,
        exclude_newsletters: excludeNewsletters,
        smart_import: smartImport,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating import settings:", error);
      toast.error("Erreur lors de la sauvegarde des paramètres");
      setSaving(false);
      return;
    }

    toast.success("Paramètres d'import configurés");
    onConfigured(data);
    
    // Start sync
    await onSync();
    setSaving(false);
  };

  return (
    <Card className="bg-primary/5 border-primary/20">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-primary">
          <Settings2 className="w-5 h-5" />
          <p className="font-medium">Configurer l'import Gmail</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Définissez vos critères avant de lancer la synchronisation
        </p>

        <Separator />

        {/* Period */}
        <div className="flex items-center justify-between">
          <Label className="text-sm">Période d'import</Label>
          <Select value={periodMonths} onValueChange={setPeriodMonths}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 derniers mois</SelectItem>
              <SelectItem value="12">12 derniers mois</SelectItem>
              <SelectItem value="36">3 dernières années</SelectItem>
              <SelectItem value="0">Tout l'historique</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Max Size */}
        <div className="flex items-center justify-between">
          <Label className="text-sm">Taille max par fichier</Label>
          <Select value={maxSizeMb} onValueChange={setMaxSizeMb}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 Mo</SelectItem>
              <SelectItem value="10">10 Mo</SelectItem>
              <SelectItem value="25">25 Mo</SelectItem>
              <SelectItem value="50">50 Mo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* File Types */}
        <div className="space-y-2">
          <Label className="text-sm">Types de fichiers</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { type: "pdf", icon: FileText, label: "PDF" },
              { type: "images", icon: FileImage, label: "Images" },
              { type: "office", icon: FileSpreadsheet, label: "Office" },
            ].map(({ type, icon: TypeIcon, label }) => {
              const isSelected = fileTypes.includes(type);
              return (
                <Badge
                  key={type}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleFileType(type)}
                >
                  <TypeIcon className="w-3 h-3 mr-1" />
                  {label}
                </Badge>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Filters */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Uniquement pièces jointes</span>
            <Switch checked={attachmentsOnly} onCheckedChange={setAttachmentsOnly} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Exclure newsletters</span>
            <Switch checked={excludeNewsletters} onCheckedChange={setExcludeNewsletters} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Import intelligent</span>
            </div>
            <Switch checked={smartImport} onCheckedChange={setSmartImport} />
          </div>
          <p className="text-xs text-muted-foreground">
            L'import intelligent identifie automatiquement les documents importants (factures, contrats, etc.)
          </p>
        </div>

        <Button 
          onClick={handleSaveAndSync} 
          disabled={saving || fileTypes.length === 0}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Configuration en cours...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sauvegarder et synchroniser
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const { t, lang, setLanguage: setAppLanguage } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<any>(null);
  
  // Profile state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  
  // Preferences state
  const [emailComments, setEmailComments] = useState(true);
  const [emailShares, setEmailShares] = useState(true);
  const [emailExpiring, setEmailExpiring] = useState(true);
  const [emailSync, setEmailSync] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Password change state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Import settings state
  const [importSettings, setImportSettings] = useState<ImportSetting[]>([]);
  const [loadingImportSettings, setLoadingImportSettings] = useState(false);

  // Organization state
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Subscription state
  const [subscription, setSubscription] = useState<{
    planName: string;
    planSlug: string;
    status: string;
    billingCycle: string;
    currentPeriodEnd: string | null;
  } | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);

  // Document optimization state (simplified - single button)
  const [optimizing, setOptimizing] = useState(false);
  const [queueStats, setQueueStats] = useState<{
    pending: number;
    processing: number;
    failed: number;
  } | null>(null);

  // Legacy state kept for advanced section
  const [reanalyzingAll, setReanalyzingAll] = useState(false);
  const [reanalyzeProgress, setReanalyzeProgress] = useState(0);
  const [reanalyzeTotal, setReanalyzeTotal] = useState(0);
  const [reanalyzeCurrent, setReanalyzeCurrent] = useState(0);

  // Gmail processing state
  const [processingGmail, setProcessingGmail] = useState(false);
  const [gmailProgress, setGmailProgress] = useState(0);
  const [gmailTotal, setGmailTotal] = useState(0);
  const [gmailCurrent, setGmailCurrent] = useState(0);
  
  // Duplicate cleanup state
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  const [duplicatesCount, setDuplicatesCount] = useState<number | null>(null);

  // Embedding generation state
  const [generatingEmbeddings, setGeneratingEmbeddings] = useState(false);
  const [embeddingsTotal, setEmbeddingsTotal] = useState<number | null>(null);
  const [embeddingsProcessed, setEmbeddingsProcessed] = useState(0);
  const [embeddingsRemaining, setEmbeddingsRemaining] = useState<number | null>(null);
  const embeddingIndexAbortRef = useRef(false);

  useEffect(() => {
    embeddingIndexAbortRef.current = false;
    return () => {
      embeddingIndexAbortRef.current = true;
    };
  }, []);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [cookieConsent, setCookieConsent] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const { canUseFeature } = useUsageLimits();
  const gmail = useGmailConnection();
  const powens = useEasySyncConnection();
  const isMobile = useIsMobile();

  // Load cookie consent preference
  useEffect(() => {
    const consent = localStorage.getItem("easyfinder_cookie_consent");
    setCookieConsent(consent);
  }, []);
  useEffect(() => {
    const loadUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      setUser(session.user);
      setEmail(session.user.email || "");
      setFullName(session.user.user_metadata?.full_name || "");

      // Load profile from database
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();

      if (profile) {
        setFullName(profile.full_name || "");
        setAvatarUrl(profile.avatar_url);
        setOrganizationId(profile.organization_id);
      }

      // Load subscription with plan details
      const { data: subData } = await supabase
        .from("subscriptions")
        .select(`
          status,
          billing_cycle,
          current_period_end,
          subscription_plans (
            name,
            slug
          )
        `)
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (subData && subData.subscription_plans) {
        setSubscription({
          planName: (subData.subscription_plans as any).name,
          planSlug: (subData.subscription_plans as any).slug,
          status: subData.status,
          billingCycle: subData.billing_cycle,
          currentPeriodEnd: subData.current_period_end,
        });
      } else {
        // Default to Discovery plan if no subscription
        setSubscription({
          planName: "Découverte",
          planSlug: "discovery",
          status: "active",
          billingCycle: "monthly",
          currentPeriodEnd: null,
        });
      }

      // Load import settings
      const { data: settings } = await supabase
        .from("import_settings")
        .select("*")
        .eq("user_id", session.user.id);

      if (settings) {
        setImportSettings(settings);
      }

      // Load notification preferences
      const { data: notifPrefs } = await supabase.rpc("get_or_create_notification_preferences", {
        p_user_id: session.user.id
      });

      if (notifPrefs) {
        setEmailComments(notifPrefs.email_comments);
        setEmailShares(notifPrefs.email_shares);
        setEmailExpiring(notifPrefs.email_expiring);
        setEmailSync(notifPrefs.email_sync);
      }

      // Initial load of document processing queue stats
      const { data: queueData } = await supabase
        .from('document_processing_queue')
        .select('status');
      
      if (queueData) {
        setQueueStats({
          pending: queueData.filter(d => d.status === 'pending').length,
          processing: queueData.filter(d => d.status === 'processing').length,
          failed: queueData.filter(d => d.status === 'failed').length,
        });
      }

      // Load count of documents without embeddings
      const { count: embeddingsCount } = await supabase
        .from('documents')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .or('embedding.is.null,embedding_ready.eq.false');
      
      if (embeddingsCount && embeddingsCount > 0) {
        setEmbeddingsRemaining(embeddingsCount);

        // If an indexing run was active before refresh, restore progress UI
        const wasActive = localStorage.getItem(LS_EMBEDDING_INDEXING_ACTIVE) === "true";
        const totalStr = localStorage.getItem(LS_EMBEDDING_INDEXING_TOTAL);
        const total = totalStr ? parseInt(totalStr, 10) : null;

        if (wasActive && total && total > 0) {
          setEmbeddingsTotal(total);
          setEmbeddingsProcessed(Math.max(0, total - embeddingsCount));
        }
      }

      setLoading(false);
    };

    loadUserData();
  }, [navigate]);

  // Auto-refresh queue stats every 10 seconds
  useEffect(() => {
    const refreshQueueStats = async () => {
      const { data: queueData } = await supabase
        .from('document_processing_queue')
        .select('status');
      
      if (queueData) {
        setQueueStats({
          pending: queueData.filter(d => d.status === 'pending').length,
          processing: queueData.filter(d => d.status === 'processing').length,
          failed: queueData.filter(d => d.status === 'failed').length,
        });
      }
    };

    // Only poll when there are items in queue
    const interval = setInterval(() => {
      if (queueStats && (queueStats.pending > 0 || queueStats.processing > 0)) {
        refreshQueueStats();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [queueStats]);

  // Resume semantic indexing after a page refresh
  useEffect(() => {
    const wasActive = localStorage.getItem(LS_EMBEDDING_INDEXING_ACTIVE) === "true";
    const remaining = embeddingsRemaining ?? 0;

    if (!user || !wasActive || generatingEmbeddings || remaining <= 0) return;

    // Fire-and-forget resume
    void (async () => {
      setGeneratingEmbeddings(true);

      try {
        const totalStr = localStorage.getItem(LS_EMBEDDING_INDEXING_TOTAL);
        const total = totalStr ? parseInt(totalStr, 10) : null;

        if (total && total > 0) {
          setEmbeddingsTotal(total);
          setEmbeddingsProcessed(Math.max(0, total - remaining));
        }

        let remainingLocal = remaining;
        while (remainingLocal > 0 && !embeddingIndexAbortRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 600));

          const { data, error } = await supabase.functions.invoke("batch-generate-embeddings", {});
          if (error) throw error;

          remainingLocal = data?.remaining || 0;
          setEmbeddingsRemaining(remainingLocal);
          if (total && total > 0) setEmbeddingsProcessed(Math.max(0, total - remainingLocal));

          // Stop condition
          if ((data?.processed || 0) === 0 && remainingLocal === 0) break;
        }

        if (!embeddingIndexAbortRef.current && remainingLocal === 0) {
          localStorage.removeItem(LS_EMBEDDING_INDEXING_ACTIVE);
          localStorage.removeItem(LS_EMBEDDING_INDEXING_TOTAL);
          setEmbeddingsTotal(null);
        }
      } catch (e) {
        // Keep flags so user can retry without losing context
        console.error("Embedding resume error:", e);
      } finally {
        if (!embeddingIndexAbortRef.current) setGeneratingEmbeddings(false);
      }
    })();
  }, [user, embeddingsRemaining, generatingEmbeddings]);

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { returnUrl: window.location.href },
      });

      // Check for error in multiple places (supabase-js puts 4xx errors differently)
      const errorMessage = error?.message || (typeof data === 'object' && data?.error) || '';
      const isNoSubscriptionError = 
        errorMessage.includes("Aucun abonnement") || 
        errorMessage.includes("No subscription") ||
        (typeof error === 'string' && error.includes("Aucun abonnement"));

      if (isNoSubscriptionError) {
        toast.info("Vous n'avez pas encore de client Stripe associé", {
          description: "Votre abonnement n'est pas lié à un compte de paiement. Contactez le support si besoin.",
        });
        return;
      }

      if (error) {
        throw error;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error("URL du portail non reçue");
      }
    } catch (err: any) {
      const errorMsg = err?.message || JSON.stringify(err) || "";
      if (errorMsg.includes("Aucun abonnement") || errorMsg.includes("No subscription")) {
        toast.info("Vous n'avez pas encore de client Stripe associé", {
          description: "Votre abonnement n'est pas lié à un compte de paiement.",
        });
      } else {
        toast.error("Impossible d'accéder au portail de gestion");
      }
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleUpdateNotificationPreference = async (field: string, value: boolean) => {
    if (!user) return;
    setSavingPrefs(true);

    // Update local state immediately
    switch (field) {
      case "email_comments":
        setEmailComments(value);
        break;
      case "email_shares":
        setEmailShares(value);
        break;
      case "email_expiring":
        setEmailExpiring(value);
        break;
      case "email_sync":
        setEmailSync(value);
        break;
    }

    const { error } = await supabase
      .from("notification_preferences")
      .update({ [field]: value })
      .eq("user_id", user.id);

    if (error) {
      toast.error("Erreur lors de la mise à jour des préférences");
      console.error(error);
    } else {
      toast.success("Préférences mises à jour");
    }
    setSavingPrefs(false);
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    // Validate input
    const trimmedName = fullName.trim();
    if (trimmedName.length > 100) {
      toast.error("Le nom ne peut pas dépasser 100 caractères");
      return;
    }

    setSaving(true);

    // Update auth metadata
    const { error: authError } = await supabase.auth.updateUser({
      data: { full_name: trimmedName }
    });

    if (authError) {
      console.error("Error updating auth:", authError);
      toast.error("Erreur lors de la mise à jour");
      setSaving(false);
      return;
    }

    // Update profile in database
    const { error: profileError } = await supabase
      .from("profiles")
      .update({ full_name: trimmedName })
      .eq("id", user.id);

    if (profileError) {
      console.error("Error updating profile:", profileError);
    }

    toast.success("Profil mis à jour");
    setSaving(false);
  };

  const handleLanguageChange = (newLang: string) => {
    setAppLanguage(newLang as Language);
    toast.success(newLang === "fr" ? t.settingsPage.language.switchedToFr : t.settingsPage.language.switchedToEn);
    // Force re-render by reloading
    window.location.reload();
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 2 Mo");
      return;
    }

    const fileExt = file.name.split(".").pop();
    const filePath = `${user.id}/avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("documents")
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      toast.error("Erreur lors de l'upload");
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("documents")
      .getPublicUrl(filePath);

    const { error: updateError } = await supabase
      .from("profiles")
      .update({ avatar_url: publicUrl })
      .eq("id", user.id);

    if (!updateError) {
      setAvatarUrl(publicUrl);
      toast.success("Photo de profil mise à jour");
    }
  };

  const handleChangePassword = async () => {
    // Validation stricte alignée avec l'inscription
    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    if (newPassword.length > 128) {
      toast.error("Le mot de passe ne doit pas dépasser 128 caractères");
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      toast.error("Le mot de passe doit contenir au moins une majuscule");
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      toast.error("Le mot de passe doit contenir au moins une minuscule");
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      toast.error("Le mot de passe doit contenir au moins un chiffre");
      return;
    }

    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      toast.error("Le mot de passe doit contenir au moins un caractère spécial");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setChangingPassword(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      toast.error("Erreur lors du changement de mot de passe");
      console.error("Password change error:", error);
    } else {
      toast.success("Mot de passe modifié avec succès");
      setNewPassword("");
      setConfirmPassword("");
    }

    setChangingPassword(false);
  };

  // Icônes de marque officielles
  const renderProviderIcon = (provider: string) => {
    switch (provider) {
      case "gmail":
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
        );
      case "outlook":
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.356.228-.593.228h-8.326V6.583h8.326c.237 0 .435.076.593.228.158.152.238.346.238.576z" />
            <path fill="#28A8EA" d="M14.843 6.583v11.086h-5.95V6.583h5.95z" />
            <path fill="#0078D4" d="M14.843 18.669H8.893v-12.086h5.95z" />
            <path fill="#50D9FF" d="M8.893 6.583v12.086H2.831c-.237 0-.435-.076-.593-.228C2.08 18.29 2 18.095 2 17.865V7.387c0-.23.08-.424.238-.576.158-.152.356-.228.593-.228h6.062z" />
            <ellipse fill="#0078D4" cx="5.5" cy="12.5" rx="3" ry="3.5" />
            <ellipse fill="#fff" cx="5.5" cy="12.5" rx="2" ry="2.5" />
          </svg>
        );
      case "whatsapp":
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        );
      default:
        return <Upload className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case "gmail": return "Gmail";
      case "outlook": return "Outlook";
      case "whatsapp": return "WhatsApp";
      default: return provider;
    }
  };

  const getPeriodLabel = (months: number | null) => {
    if (!months || months === 0) return "Tout l'historique";
    if (months === 3) return "3 derniers mois";
    if (months === 12) return "12 derniers mois";
    if (months === 36) return "3 dernières années";
    return `${months} mois`;
  };

  const handleUpdateImportSetting = async (settingId: string, field: string, value: any) => {
    const { error } = await supabase
      .from("import_settings")
      .update({ [field]: value })
      .eq("id", settingId);

    if (error) {
      toast.error("Erreur lors de la mise à jour");
      return;
    }

    setImportSettings(prev => 
      prev.map(s => s.id === settingId ? { ...s, [field]: value } : s)
    );
    toast.success("Paramètre mis à jour");
  };

  const handleDeleteImportSetting = async (settingId: string) => {
    const { error } = await supabase
      .from("import_settings")
      .delete()
      .eq("id", settingId);

    if (error) {
      toast.error("Erreur lors de la suppression");
      return;
    }

    setImportSettings(prev => prev.filter(s => s.id !== settingId));
    toast.success("Source déconnectée");
  };

  // ========================================================================
  // SIMPLIFIED: Single "Optimize Documents" button that enqueues server-side
  // This replaces the multiple technical buttons (reanalyze, gmail, etc.)
  // Handles 3 UX cases:
  // 1. Documents already in queue → Show "X en attente, traitement en cours"
  // 2. New documents enqueued → Show "X documents ajoutés"
  // 3. Nothing to do → Show "Tous optimisés"
  // ========================================================================
  const handleOptimizeDocuments = async () => {
    if (!user) return;
    
    setOptimizing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('batch-enqueue-reprocess', {
        body: {
          filters: {
            needsReview: true,
            lowConfidence: true,
            typeOther: true,
            gmailProcessing: true,
          },
          limit: 1000
        }
      });

      if (error) throw error;

      const { total = 0, enqueued = 0, skipped = 0 } = data || {};

      // Refresh queue stats after enqueue
      const { data: queueData } = await supabase
        .from('document_processing_queue')
        .select('status');
      
      const newQueueStats = queueData ? {
        pending: queueData.filter(d => d.status === 'pending').length,
        processing: queueData.filter(d => d.status === 'processing').length,
        failed: queueData.filter(d => d.status === 'failed').length,
      } : null;
      
      setQueueStats(newQueueStats);
      const queuePending = newQueueStats?.pending ?? 0;

      // Case 1: No new documents, but some are already in queue
      if (enqueued === 0 && queuePending > 0) {
        const estimatedMinutes = Math.ceil(queuePending / 10);
        toast.info(`${queuePending} documents en attente de traitement`, {
          description: `~${estimatedMinutes} min restantes (traitement automatique)`
        });
      }
      // Case 2: No new documents enqueued, queue empty, but some were skipped (already processed recently)
      else if (enqueued === 0 && queuePending === 0 && skipped > 0) {
        toast.info(`${skipped} documents ignorés`, {
          description: "Documents analysés récemment ou déjà en cours de traitement."
        });
      }
      // Case 3: No new documents, queue empty, nothing was eligible at all
      else if (enqueued === 0 && queuePending === 0 && skipped === 0) {
        toast.success("Tous vos documents sont déjà optimisés !");
      }
      // Case 4: New documents were enqueued
      else if (enqueued > 0) {
        const estimatedMinutes = Math.ceil(queuePending / 10);
        toast.success(`${enqueued} documents ajoutés à la file d'optimisation`, {
          description: queuePending > enqueued 
            ? `${queuePending} documents au total (~${estimatedMinutes} min).`
            : "Le traitement démarre automatiquement."
        });
      }
    } catch (err) {
      console.error("Optimize error:", err);
      toast.error("Erreur lors de l'optimisation");
    } finally {
      setOptimizing(false);
    }
  };

  // Legacy function kept for backwards compatibility (now unused in UI)
  const handleReanalyzeAllDocuments = async () => {
    if (!user) return;
    
    setReanalyzingAll(true);
    setReanalyzeProgress(0);
    setReanalyzeCurrent(0);
    
    try {
      // Fetch all user documents
      const { data: documents, error } = await supabase
        .from("documents")
        .select("id, file_name, mime_type, doc_type, file_path")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      if (!documents || documents.length === 0) {
        toast.info("Aucun document à réanalyser");
        setReanalyzingAll(false);
        return;
      }
      
      setReanalyzeTotal(documents.length);
      toast.info(`Réanalyse V2 de ${documents.length} documents en cours...`);
      
      let successCount = 0;
      let errorCount = 0;
      const BATCH_SIZE = 3; // Reduced batch size for V2 (vision API is slower)
      
      // Process documents in parallel batches
      for (let i = 0; i < documents.length; i += BATCH_SIZE) {
        const batch = documents.slice(i, Math.min(i + BATCH_SIZE, documents.length));
        const batchEndIndex = Math.min(i + BATCH_SIZE, documents.length);
        
        // Process batch in parallel using V2 classification
        const results = await Promise.allSettled(
          batch.map(async (doc) => {
            // Use classify-document-v2 for vision-based classification
            const { data: v2Result, error: v2Error } = await supabase.functions.invoke('classify-document-v2', {
              body: {
                fileName: doc.file_name,
                mimeType: doc.mime_type,
                filePath: doc.file_path,
              }
            });

            if (v2Error) {
              throw v2Error;
            }

            if (v2Result) {
              const updateData: Record<string, unknown> = {
                taxonomy_slug: v2Result.taxonomy_slug || null,
                doc_type: v2Result.core_type || v2Result.document_type || doc.doc_type,
                category: v2Result.category || 'other',
                data_sensitivity: v2Result.data_sensitivity || 'medium',
                extracted_data: v2Result.extracted_data || {},
                suggested_actions: v2Result.suggested_actions || [],
                confidence_score: v2Result.confidence_score,
                status: v2Result.status || 'complete',
                classification_method: 'ai_v2',
                updated_at: new Date().toISOString(),
              };
              
              // Add issuer_name_norm if present (critical for search)
              if (v2Result.issuer_name_norm) {
                updateData.issuer_name_norm = v2Result.issuer_name_norm;
              }
              
              // Add universal fields if present
              const uf = v2Result.universal_fields;
              if (uf) {
                if (uf.issuer_name) updateData.issuer_name = uf.issuer_name;
                if (uf.issue_date) updateData.issue_date = uf.issue_date;
                if (uf.due_date) updateData.due_date = uf.due_date;
                if (uf.total_amount != null) updateData.total_amount = uf.total_amount;
                if (uf.reference_number) updateData.reference_number = uf.reference_number;
                if (uf.recipient_name) updateData.recipient_name = uf.recipient_name;
              }
              
              await supabase
                .from("documents")
                .update(updateData)
                .eq("id", doc.id);
              
              return true;
            }
            return false;
          })
        );
        
        // Count results
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            successCount++;
          } else {
            errorCount++;
            if (result.status === 'rejected') {
              console.error('Batch V2 processing error:', result.reason);
            }
          }
        });
        
        // Update progress after each batch
        setReanalyzeCurrent(batchEndIndex);
        setReanalyzeProgress(Math.round((batchEndIndex / documents.length) * 100));
        
        // Add a small delay between batches to avoid rate limits
        if (i + BATCH_SIZE < documents.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (errorCount > 0) {
        toast.warning(`Réanalyse V2 terminée: ${successCount} succès, ${errorCount} erreurs`);
      } else {
        toast.success(`Réanalyse V2 terminée: ${successCount} documents mis à jour`);
      }
    } catch (err) {
      console.error("Batch V2 reanalyze error:", err);
      toast.error("Erreur lors de la réanalyse V2");
    } finally {
      setReanalyzingAll(false);
      setReanalyzeProgress(0);
      setReanalyzeCurrent(0);
      setReanalyzeTotal(0);
    }
  };

  // Process Gmail documents stuck in 'processing' status
  const handleProcessGmailDocuments = async () => {
    if (!user) return;
    
    setProcessingGmail(true);
    setGmailProgress(0);
    setGmailCurrent(0);
    
    try {
      // Fetch Gmail docs in 'processing' status
      const { data: gmailDocs, error } = await supabase
        .from("documents")
        .select("id, file_name, mime_type, file_path")
        .eq("user_id", user.id)
        .eq("source", "gmail")
        .eq("status", "processing");
      
      if (error) {
        throw error;
      }
      
      if (!gmailDocs || gmailDocs.length === 0) {
        toast.info("Aucun document Gmail en attente de traitement");
        setProcessingGmail(false);
        return;
      }
      
      setGmailTotal(gmailDocs.length);
      toast.info(`Traitement de ${gmailDocs.length} documents Gmail...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Process in parallel batches of 5
      const batchSize = 5;
      for (let i = 0; i < gmailDocs.length; i += batchSize) {
        const batch = gmailDocs.slice(i, Math.min(i + batchSize, gmailDocs.length));
        const batchEndIndex = Math.min(i + batchSize, gmailDocs.length);
        
        const results = await Promise.allSettled(
          batch.map(async (doc) => {
            try {
              // Call extract-document-info edge function
              const { data: extractedInfo, error: extractError } = await supabase.functions.invoke(
                "extract-document-info",
                {
                  body: {
                    fileName: doc.file_name,
                    mimeType: doc.mime_type || "application/pdf",
                    filePath: doc.file_path,
                  },
                }
              );
              
              if (extractError) {
                console.error(`Error extracting ${doc.file_name}:`, extractError);
                // Even if extraction fails, mark as complete so it's visible
                await supabase
                  .from("documents")
                  .update({ status: 'complete' })
                  .eq("id", doc.id);
                return false;
              }
              
              // Update document with extracted info
              // Note: Do NOT update taxonomy_slug here - preserve existing or leave null for V2/manual
              const updateData: Record<string, unknown> = {
                title: extractedInfo.title || doc.file_name,
                ai_summary: extractedInfo.ai_summary,
                doc_type: extractedInfo.doc_type || 'other',
                extracted_text: extractedInfo.extracted_text,
                text_extracted: !!extractedInfo.extracted_text,
                ocr_used: extractedInfo.ocr_used || false,
                doc_date: extractedInfo.doc_date,
                expires_at: extractedInfo.expires_at,
                keywords: extractedInfo.keywords,
                confidence_score: extractedInfo.confidence_score,
                is_sensitive: extractedInfo.is_sensitive,
                status: 'complete',
                classification_method: 'legacy_extract',
              };
              
              if (extractedInfo.category) {
                updateData.category = extractedInfo.category;
              }
              
              await supabase
                .from("documents")
                .update(updateData)
                .eq("id", doc.id);
              
              return true;
            } catch (err) {
              console.error(`Error processing ${doc.file_name}:`, err);
              // Mark as complete even on error
              await supabase
                .from("documents")
                .update({ status: 'complete' })
                .eq("id", doc.id);
              return false;
            }
          })
        );
        
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            successCount++;
          } else {
            errorCount++;
          }
        });
        
        setGmailCurrent(batchEndIndex);
        setGmailProgress(Math.round((batchEndIndex / gmailDocs.length) * 100));
      }
      
      if (errorCount > 0) {
        toast.warning(`Traitement terminé: ${successCount} analysés, ${errorCount} avec erreurs (visibles quand même)`);
      } else {
        toast.success(`${successCount} documents Gmail traités avec succès !`);
      }
    } catch (err) {
      console.error("Gmail processing error:", err);
      toast.error("Erreur lors du traitement des documents Gmail");
    } finally {
      setProcessingGmail(false);
      setGmailProgress(0);
      setGmailCurrent(0);
      setGmailTotal(0);
    }
  };

  // Clean up Gmail duplicates
  const handleCleanGmailDuplicates = async () => {
    if (!user) return;
    
    setCleaningDuplicates(true);
    
    try {
      // Fetch all Gmail documents with more identifying fields
      const { data: duplicates, error: fetchError } = await supabase
        .from("documents")
        .select("id, source_url, file_name, file_size, file_hash, content_fingerprint, created_at")
        .eq("user_id", user.id)
        .eq("source", "gmail")
        .not("source_url", "is", null)
        .order("created_at", { ascending: true });
      
      if (fetchError) throw fetchError;
      
      if (!duplicates || duplicates.length === 0) {
        toast.info("Aucun document Gmail trouvé");
        setCleaningDuplicates(false);
        return;
      }
      
      // Find TRUE duplicates using multiple criteria:
      // 1. If file_hash exists, use source_url + file_hash (most reliable)
      // 2. If content_fingerprint exists, use source_url + content_fingerprint
      // 3. Otherwise use source_url + file_name + file_size (all three must match)
      const seen = new Map<string, string>(); // key -> oldest id
      const toDelete: string[] = [];
      
      for (const doc of duplicates) {
        let key: string;
        
        if (doc.file_hash) {
          // Most reliable: same email + same file content hash
          key = `hash:${doc.source_url}|${doc.file_hash}`;
        } else if (doc.content_fingerprint) {
          // Good: same email + same content fingerprint
          key = `fp:${doc.source_url}|${doc.content_fingerprint}`;
        } else {
          // Fallback: require ALL THREE to match (url + name + size)
          // This prevents false positives from files with same name but different content
          key = `meta:${doc.source_url}|${doc.file_name}|${doc.file_size || 'unknown'}`;
        }
        
        if (seen.has(key)) {
          toDelete.push(doc.id);
        } else {
          seen.set(key, doc.id);
        }
      }
      
      if (toDelete.length === 0) {
        toast.success("Aucun doublon détecté");
        setCleaningDuplicates(false);
        setDuplicatesCount(0);
        return;
      }
      
      setDuplicatesCount(toDelete.length);
      
      // Delete duplicates in batches
      const batchSize = 50;
      for (let i = 0; i < toDelete.length; i += batchSize) {
        const batch = toDelete.slice(i, i + batchSize);
        const { error: deleteError } = await supabase
          .from("documents")
          .delete()
          .in("id", batch);
        
        if (deleteError) {
          console.error("Delete batch error:", deleteError);
        }
      }
      
      toast.success(`${toDelete.length} doublons supprimés`);
      setDuplicatesCount(0);
    } catch (err) {
      console.error("Duplicate cleanup error:", err);
      toast.error("Erreur lors du nettoyage des doublons");
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // Generate embeddings for semantic search
  const handleGenerateEmbeddings = async () => {
    if (!user) return;
    
    setGeneratingEmbeddings(true);
    setEmbeddingsProcessed(0);
    
    try {
      localStorage.setItem(LS_EMBEDDING_INDEXING_ACTIVE, "true");

      // First call to get initial count
      const { data: initialData, error: initialError } = await supabase.functions.invoke("batch-generate-embeddings", {});
      
      if (initialError) {
        console.error("Embedding error:", initialError);
        throw initialError;
      }
      
      // Calculate initial total from first response
      const initialTotal = (initialData.processed || 0) + (initialData.remaining || 0);
      setEmbeddingsTotal(initialTotal);
      localStorage.setItem(LS_EMBEDDING_INDEXING_TOTAL, String(initialTotal));
      
      let totalProcessed = initialData.processed || 0;
      let remaining = initialData.remaining || 0;
      setEmbeddingsProcessed(totalProcessed);
      setEmbeddingsRemaining(remaining);
      
      // Continue processing if there are more
      while (remaining > 0) {
        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 500));

        if (embeddingIndexAbortRef.current) break;
        
        const { data, error } = await supabase.functions.invoke("batch-generate-embeddings", {});
        
        if (error) {
          console.error("Embedding error:", error);
          throw error;
        }
        
        totalProcessed += data.processed || 0;
        remaining = data.remaining || 0;
        setEmbeddingsProcessed(totalProcessed);
        setEmbeddingsRemaining(remaining);
        
        if (data.processed === 0 && remaining === 0) {
          break;
        }
      }
      
      if (totalProcessed > 0) {
        toast.success(`${totalProcessed} documents indexés pour la recherche sémantique`);
      } else {
        toast.info("Tous les documents sont déjà indexés");
      }
      setEmbeddingsRemaining(0);
      setEmbeddingsTotal(null);
      localStorage.removeItem(LS_EMBEDDING_INDEXING_ACTIVE);
      localStorage.removeItem(LS_EMBEDDING_INDEXING_TOTAL);
    } catch (err) {
      console.error("Embedding generation error:", err);
      toast.error("Erreur lors de l'indexation sémantique");
    } finally {
      setGeneratingEmbeddings(false);
    }
  };

  const handleExportAll = async () => {
    if (!user) return;
    
    setExporting(true);
    toast.info("Préparation de l'export...");

    try {
      const { data, error } = await supabase.functions.invoke("export-documents", {});

      if (error) {
        console.error("Export error:", error);
        throw error;
      }

      // Check if response is an error JSON
      if (data && typeof data === "object" && data.error) {
        throw new Error(data.error);
      }

      // data should be the blob/arraybuffer from the edge function
      // Create download link
      const blob = new Blob([data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `easyfinder-export-${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Export terminé ! Téléchargement en cours...");
    } catch (err: any) {
      console.error("Export error:", err);
      toast.error(err.message || "Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background md:ml-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Build connected sources for display
  const connectedSources = [
    ...(gmail.status === "connected" ? [{
      id: "gmail",
      provider: "gmail",
      status: gmail.connection?.status || "active",
      lastSyncAt: gmail.connection?.last_sync_at || null,
    }] : []),
    ...(powens.status === "connected" ? [{
      id: "powens",
      provider: "powens",
      status: "active",
      lastSyncAt: powens.connections?.[0]?.last_sync_at || null,
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-background md:ml-16">
      <SEO 
        title="Paramètres"
        description="Configurez votre compte EasyFinder : profil, notifications, intégrations et préférences."
        noIndex={true}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border safe-area-top">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/documents")}
            className="h-9 w-9"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">Paramètres</h1>
        </div>
      </header>

      {/* Desktop: Two-column layout */}
      <div className={isMobile ? "container mx-auto px-4 py-6 pb-24" : "container mx-auto px-6 py-8 max-w-5xl"}>
        <div className={isMobile ? "" : "grid grid-cols-[280px_1fr] gap-8"}>
          {/* Desktop sidebar - Sources connectées summary */}
          {!isMobile && (
            <aside className="space-y-6">
              <div className="sticky top-24">
                <ConnectedSourcesCard 
                  sources={connectedSources}
                  onSync={(provider) => {
                    if (provider === "gmail") gmail.sync?.();
                    if (provider === "powens") powens.sync?.();
                  }}
                />
                
                {/* Quick navigation */}
                <div className="mt-6 p-4 rounded-xl bg-secondary/30 border border-border/50">
                  <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">Navigation rapide</p>
                  <nav className="space-y-1">
                    {[
                      { label: "Profil", id: "profile" },
                      { label: "Abonnement", id: "subscription" },
                      { label: "Connexions", id: "connections" },
                      { label: "Notifications", id: "notifications" },
                      { label: "Sécurité", id: "security" },
                    ].map((item) => (
                      <button
                        key={item.id}
                        onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className="block w-full text-left px-3 py-2 text-sm rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        {item.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          )}

          {/* Main content */}
          <main className="space-y-6">
            {/* Profile Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              <CardTitle>Profil</CardTitle>
            </div>
            <CardDescription>Gérez vos informations personnelles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="w-20 h-20 shadow-lg">
                  <AvatarImage src={avatarUrl || undefined} />
                  <AvatarFallback className="text-lg bg-gradient-to-br from-primary/20 to-primary/5 text-primary">
                    {getInitials(fullName || email)}
                  </AvatarFallback>
                </Avatar>
                <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all shadow-md">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </label>
              </div>
              <div>
                <p className="font-medium">{fullName || "Utilisateur"}</p>
                <p className="text-sm text-muted-foreground">{email}</p>
              </div>
            </div>

            <Separator />

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="fullName">{t.settingsPage.profile.fullNameLabel}</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t.settingsPage.profile.fullNamePlaceholder}
                maxLength={100}
              />
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email">{t.settingsPage.profile.emailLabel}</Label>
              <Input id="email" value={email} disabled className="bg-secondary/50" />
              <p className="text-xs text-muted-foreground">{t.settingsPage.profile.emailReadonly}</p>
            </div>

            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.settingsPage.profile.saving}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t.settingsPage.profile.save}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Subscription Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              <CardTitle>{t.settingsPage.subscription.title}</CardTitle>
            </div>
            <CardDescription>{t.settingsPage.subscription.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscription && (
              <>
                {/* Current Plan */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${
                      subscription.planSlug === 'discovery' ? 'bg-gradient-to-br from-emerald-500 to-teal-500' :
                      subscription.planSlug === 'personal' ? 'bg-gradient-to-br from-blue-500 to-indigo-500' :
                      subscription.planSlug === 'pro' ? 'bg-gradient-to-br from-primary to-purple-500' :
                      'bg-gradient-to-br from-slate-600 to-slate-800'
                    }`}>
                      {subscription.planSlug === 'discovery' ? <Zap className="w-6 h-6" /> :
                       subscription.planSlug === 'personal' ? <FileText className="w-6 h-6" /> :
                       subscription.planSlug === 'pro' ? <Sparkles className="w-6 h-6" /> :
                       <Building2 className="w-6 h-6" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-lg">{subscription.planName}</p>
                        <Badge
                          variant={
                            subscription.status === 'active'
                              ? 'success'
                              : subscription.status === 'canceled'
                                ? 'destructive'
                                : 'secondary'
                          }
                        >
                          {subscription.status === 'active'
                            ? t.settingsPage.subscription.statusActive
                            : subscription.status === 'canceled'
                              ? t.settingsPage.subscription.statusCanceled
                              : subscription.status === 'trialing'
                                ? t.settingsPage.subscription.statusTrial
                                : subscription.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {subscription.planSlug === 'discovery'
                          ? t.settingsPage.subscription.freePlanNote
                          : t.settingsPage.subscription.billing.replace(
                              '{cycle}',
                              subscription.billingCycle === 'yearly'
                                ? t.settingsPage.subscription.billingYearly
                                : t.settingsPage.subscription.billingMonthly
                            )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Billing Info */}
                {subscription.planSlug !== 'discovery' && subscription.currentPeriodEnd && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t.settingsPage.billing.nextBilling}</span>
                    <span className="font-medium">
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString(
                        lang === 'en' ? 'en-US' : 'fr-FR',
                        { day: 'numeric', month: 'long', year: 'numeric' }
                      )}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  {subscription.planSlug === 'discovery' ? (
                    <Button onClick={() => navigate('/pricing')} className="gap-2">
                      <Sparkles className="w-4 h-4" />
                      {t.settingsPage.billing.upgrade}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleManageSubscription}
                        disabled={loadingPortal}
                        className="gap-2"
                      >
                        {loadingPortal ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4" />
                        )}
                        {t.settingsPage.billing.manageBilling}
                      </Button>
                      <Button variant="ghost" onClick={() => navigate('/pricing')} className="gap-2">
                        <ExternalLink className="w-4 h-4" />
                        {t.settingsPage.billing.viewPlans}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Language Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              <CardTitle>{t.settingsPage.language.title}</CardTitle>
            </div>
            <CardDescription>{t.settingsPage.language.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={lang} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 Français</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Import Settings Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              <CardTitle>{t.settingsPage.import.title}</CardTitle>
            </div>
            <CardDescription>{t.settingsPage.import.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Gmail Connection */}
            <div className="p-4 rounded-lg border border-border/50 bg-secondary/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      gmail.status === "connected"
                        ? "bg-emerald-500/10 text-emerald-500"
                        : gmail.status === "expired"
                          ? "bg-amber-500/10 text-amber-500"
                          : "bg-primary/10 text-primary"
                    }`}
                  >
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">Gmail</p>
                      {gmail.status === "connected" && (
                        <Badge variant="success" className="text-xs">
                          {t.settingsPage.gmail.connected}
                        </Badge>
                      )}
                      {gmail.status === "expired" && (
                        <Badge variant="secondary" className="text-xs text-amber-600">
                          {t.settingsPage.gmail.reconnectRequired}
                        </Badge>
                      )}
                    </div>
                    {gmail.connection?.last_sync_at && (
                      <p className="text-xs text-muted-foreground">
                        {t.settingsPage.gmail.lastSync}:{" "}
                        {new Date(gmail.connection.last_sync_at).toLocaleDateString(
                          lang === "en" ? "en-US" : "fr-FR"
                        )}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {gmail.status === "connected" ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={gmail.sync}
                        disabled={gmail.syncing}
                      >
                        {gmail.syncing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        <span className="ml-2 hidden sm:inline">{t.settingsPage.gmail.sync}</span>
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={gmail.disconnect}
                        title={t.settingsPage.gmail.disconnectTitle}
                      >
                        <X className="w-4 h-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={async () => {
                          if (!confirm(t.settingsPage.gmail.deleteAllConfirm)) return;
                          try {
                            const { deletedCount } = await gmail.deleteGmailData();
                            setImportSettings((prev) => prev.filter((s) => s.provider !== "gmail"));
                            toast.success(
                              t.settingsPage.gmail.deletedDocs.replace("{count}", String(deletedCount))
                            );
                          } catch (err) {
                            console.error("Error deleting Gmail data:", err);
                            toast.error(t.settingsPage.gmail.deleteError);
                          }
                        }}
                        title={t.settingsPage.gmail.deleteAllTitle}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  ) : gmail.status === "expired" ? (
                    <Button
                      onClick={gmail.connect}
                      disabled={gmail.connecting}
                      variant="outline"
                      size="sm"
                    >
                      {gmail.connecting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      {t.settingsPage.gmail.reconnect}
                    </Button>
                  ) : (
                    <Button onClick={gmail.connect} disabled={gmail.connecting} size="sm">
                      {gmail.connecting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      {t.settingsPage.gmail.connect}
                    </Button>
                  )}
                </div>
              </div>

              {/* Gmail Sync Progress */}
              {gmail.status === "connected" && (gmail.syncing || gmail.syncState?.status !== "idle") && (
                <GmailSyncProgress
                  syncState={gmail.syncState}
                  syncing={gmail.syncing}
                  onCancel={gmail.cancelSync}
                  onReset={gmail.resetSyncState}
                  className="mt-3"
                />
              )}
            </div>

            {/* Gmail Import Configuration - shown when connected but no import_settings yet */}
            {gmail.status === "connected" && !importSettings.some((s) => s.provider === "gmail") && (
              <GmailImportConfig
                userId={user?.id}
                onConfigured={(setting) => setImportSettings((prev) => [...prev, setting])}
                onSync={gmail.sync}
              />
            )}

            {/* Official Documents - Powens */}
            <OfficialDocumentsCard
              status={powens.status}
              connections={powens.connections}
              loading={powens.loading}
              syncing={powens.syncing}
              syncStatus={powens.syncStatus}
              lastSyncResult={powens.lastSyncResult}
              onConnect={powens.connect}
              onDisconnect={powens.disconnect}
              onSync={powens.sync}
            />

            {/* Separator if there are also import settings */}
            {importSettings.length > 0 && <Separator className="my-4" />}

            {/* Existing Import Settings */}
            {importSettings.length === 0 && gmail.status !== "connected" ? (
              <div className="text-center py-6">
                <Settings2 className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground text-sm">{t.settingsPage.import.noneConfigured}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.settingsPage.import.connectGmailHint}</p>
              </div>
            ) : null}
            
            {importSettings.length > 0 && (
              importSettings.map((setting) => {
                return (
                  <Card key={setting.id} className="bg-secondary/30">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                            {renderProviderIcon(setting.provider)}
                          </div>
                          <div>
                            <p className="font-medium">{getProviderLabel(setting.provider)}</p>
                            <p className="text-xs text-muted-foreground">
                              Dernière synchro: {setting.updated_at ? new Date(setting.updated_at).toLocaleDateString("fr-FR") : "Jamais"}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteImportSetting(setting.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <Separator />

                      {/* Period */}
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Période d'import</Label>
                        <Select 
                          value={setting.period_months?.toString() || "12"} 
                          onValueChange={(v) => handleUpdateImportSetting(setting.id, "period_months", parseInt(v))}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3 derniers mois</SelectItem>
                            <SelectItem value="12">12 derniers mois</SelectItem>
                            <SelectItem value="36">3 dernières années</SelectItem>
                            <SelectItem value="0">Tout l'historique</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Max Size */}
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Taille max par fichier</Label>
                        <Select 
                          value={setting.max_size_mb?.toString() || "25"} 
                          onValueChange={(v) => handleUpdateImportSetting(setting.id, "max_size_mb", parseInt(v))}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">5 Mo</SelectItem>
                            <SelectItem value="10">10 Mo</SelectItem>
                            <SelectItem value="25">25 Mo</SelectItem>
                            <SelectItem value="50">50 Mo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* File Types */}
                      <div className="space-y-2">
                        <Label className="text-sm">Types de fichiers</Label>
                        <div className="flex flex-wrap gap-2">
                          {["pdf", "images", "office"].map((type) => {
                            const isSelected = setting.file_types?.includes(type) ?? true;
                            const icons: Record<string, any> = {
                              pdf: FileText,
                              images: FileImage,
                              office: FileSpreadsheet
                            };
                            const labels: Record<string, string> = {
                              pdf: "PDF",
                              images: "Images",
                              office: "Office"
                            };
                            const TypeIcon = icons[type];
                            return (
                              <Badge
                                key={type}
                                variant={isSelected ? "default" : "outline"}
                                className="cursor-pointer"
                                onClick={() => {
                                  const currentTypes = setting.file_types || ["pdf", "images", "office"];
                                  const newTypes = isSelected
                                    ? currentTypes.filter(t => t !== type)
                                    : [...currentTypes, type];
                                  if (newTypes.length > 0) {
                                    handleUpdateImportSetting(setting.id, "file_types", newTypes);
                                  }
                                }}
                              >
                                <TypeIcon className="w-3 h-3 mr-1" />
                                {labels[type]}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>

                      <Separator />

                      {/* Filters */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Uniquement pièces jointes</span>
                          <Switch 
                            checked={setting.attachments_only ?? true}
                            onCheckedChange={(v) => handleUpdateImportSetting(setting.id, "attachments_only", v)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Exclure newsletters</span>
                          <Switch 
                            checked={setting.exclude_newsletters ?? true}
                            onCheckedChange={(v) => handleUpdateImportSetting(setting.id, "exclude_newsletters", v)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium">Import intelligent</span>
                          </div>
                          <Switch 
                            checked={setting.smart_import ?? true}
                            onCheckedChange={(v) => handleUpdateImportSetting(setting.id, "smart_import", v)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Document Maintenance Section - SIMPLIFIED */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <CardTitle>Optimisation des documents</CardTitle>
            </div>
            <CardDescription>
              Améliorez automatiquement la qualité d'analyse de vos documents
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Main action - single button with queue status */}
            <div className="flex flex-col gap-3">
              <div>
                <p className="font-medium">Optimiser tous mes documents</p>
                <p className="text-sm text-muted-foreground">
                  Relance l'analyse IA sur les documents qui en ont besoin : 
                  faible confiance, type "autre", imports incomplets...
                </p>
              </div>
              
              {/* Queue status indicator */}
              {queueStats && (queueStats.pending > 0 || queueStats.processing > 0) && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {queueStats.pending + queueStats.processing} document{(queueStats.pending + queueStats.processing) > 1 ? 's' : ''} en cours de traitement
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Le traitement se fait automatiquement toutes les 2 minutes
                    </p>
                  </div>
                </div>
              )}
              
              {/* Failed documents warning */}
              {queueStats && queueStats.failed > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-destructive">
                      {queueStats.failed} document{queueStats.failed > 1 ? 's' : ''} en échec
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Ces documents seront réessayés automatiquement
                    </p>
                  </div>
                </div>
              )}
              
              <Button 
                onClick={handleOptimizeDocuments} 
                disabled={optimizing}
                className="w-fit"
              >
                {optimizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Vérification en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {queueStats && (queueStats.pending > 0 || queueStats.processing > 0) 
                      ? "Vérifier l'état" 
                      : "Optimiser mes documents"}
                  </>
                )}
              </Button>
            </div>

            {/* Advanced section - collapsed by default */}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between mt-4">
                  <span className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4" />
                    Options avancées
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <Separator />
                
                {/* Gmail Duplicates Cleanup */}
                <div className="flex flex-col gap-3">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-destructive" />
                      Supprimer les doublons
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Supprime les documents en double (garde le plus ancien)
                    </p>
                  </div>
                  
                  {duplicatesCount !== null && duplicatesCount > 0 && (
                    <p className="text-sm text-destructive">
                      {duplicatesCount} doublons détectés
                    </p>
                  )}
                  
                  <Button 
                    onClick={handleCleanGmailDuplicates} 
                    disabled={cleaningDuplicates || optimizing}
                    variant="outline"
                    size="sm"
                    className="w-fit"
                  >
                    {cleaningDuplicates ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Nettoyage...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Nettoyer les doublons
                      </>
                    )}
                  </Button>
                </div>
                
                <Separator />
                
                {/* Semantic Search Indexing */}
                <div className="flex flex-col gap-3">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Indexation recherche sémantique
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Génère les embeddings pour la recherche en langage naturel
                    </p>
                  </div>
                  
                  {embeddingsRemaining !== null && embeddingsRemaining > 0 && !generatingEmbeddings && (
                    <p className="text-sm text-primary">
                      {embeddingsRemaining} documents à indexer
                    </p>
                  )}
                  
                  {/* Progress bar during indexation */}
                  {generatingEmbeddings && embeddingsTotal !== null && embeddingsTotal > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {embeddingsProcessed} / {embeddingsTotal} documents
                        </span>
                        <span className="font-medium text-primary">
                          {Math.round((embeddingsProcessed / embeddingsTotal) * 100)}%
                        </span>
                      </div>
                      <Progress 
                        value={(embeddingsProcessed / embeddingsTotal) * 100} 
                        className="h-2"
                      />
                      {embeddingsRemaining !== null && embeddingsRemaining > 0 && (
                        <p className="text-xs text-muted-foreground">
                          ~{Math.ceil(embeddingsRemaining / 10)} minutes restantes
                        </p>
                      )}
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleGenerateEmbeddings} 
                    disabled={generatingEmbeddings || optimizing}
                    variant="outline"
                    size="sm"
                    className="w-fit"
                  >
                    {generatingEmbeddings ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Indexation en cours...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Indexer pour recherche
                      </>
                    )}
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        {/* Export Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileArchive className="w-5 h-5 text-primary" />
              <CardTitle>Export de données</CardTitle>
            </div>
            <CardDescription>Téléchargez une copie complète de vos documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {canUseFeature("export") ? (
              <div className="space-y-3">
                <div>
                  <p className="font-medium">Exporter tous mes documents</p>
                  <p className="text-sm text-muted-foreground">
                    Téléchargez un fichier ZIP contenant tous vos documents ainsi que leurs métadonnées au format JSON et CSV
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">
                    <FileText className="w-3 h-3 mr-1" />
                    Documents PDF/Images
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <FileSpreadsheet className="w-3 h-3 mr-1" />
                    Métadonnées CSV
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Résumés IA JSON
                  </Badge>
                </div>
                
                <Button 
                  onClick={handleExportAll} 
                  disabled={exporting}
                  variant="outline"
                  className="w-fit"
                >
                  {exporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger l'export complet
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <UpgradePrompt 
                feature="Export de données" 
                description="Téléchargez une copie complète de vos documents avec métadonnées. Disponible avec un abonnement payant."
                minPlan="Perso"
              />
            )}
          </CardContent>
        </Card>

        {/* Team Management Section */}
        <div className="mb-6">
          <TeamManagement organizationId={organizationId} currentUserId={user?.id || ""} />
        </div>

        {/* Notifications Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              <CardTitle>Notifications</CardTitle>
            </div>
            <CardDescription>Gérez vos préférences de notifications email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Push Notifications Toggle */}
            <PushNotificationToggle />
            
            <Separator />

            <p className="text-sm font-medium text-muted-foreground">Notifications par email</p>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-primary" />
                  Commentaires
                </p>
                <p className="text-sm text-muted-foreground">
                  Recevez un email quand quelqu'un commente vos documents
                </p>
              </div>
              <Switch
                checked={emailComments}
                onCheckedChange={(v) => handleUpdateNotificationPreference("email_comments", v)}
                disabled={savingPrefs}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Upload className="w-4 h-4 text-primary" />
                  Partages
                </p>
                <p className="text-sm text-muted-foreground">
                  Recevez un email quand quelqu'un partage un document avec vous
                </p>
              </div>
              <Switch
                checked={emailShares}
                onCheckedChange={(v) => handleUpdateNotificationPreference("email_shares", v)}
                disabled={savingPrefs}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4 text-primary" />
                  Alertes d'expiration
                </p>
                <p className="text-sm text-muted-foreground">
                  Soyez notifié par email avant l'expiration de vos documents
                </p>
              </div>
              <Switch
                checked={emailExpiring}
                onCheckedChange={(v) => handleUpdateNotificationPreference("email_expiring", v)}
                disabled={savingPrefs}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Synchronisation
                </p>
                <p className="text-sm text-muted-foreground">
                  Notifications lors de l'import de nouveaux documents
                </p>
              </div>
              <Switch
                checked={emailSync}
                onCheckedChange={(v) => handleUpdateNotificationPreference("email_sync", v)}
                disabled={savingPrefs}
              />
            </div>
          </CardContent>
        </Card>

        {/* Referral Section */}
        <ReferralSection />

        {/* Security Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <CardTitle>Sécurité</CardTitle>
            </div>
            <CardDescription>Gérez la sécurité de votre compte</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Biometric Authentication */}
            <BiometricSettings />
            {/* Password Change Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Au moins 6 caractères"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Répétez le mot de passe"
                />
              </div>

              <Button 
                onClick={handleChangePassword} 
                disabled={changingPassword || !newPassword || !confirmPassword}
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Modification...
                  </>
                ) : (
                  "Changer le mot de passe"
                )}
              </Button>
            </div>

            <Separator />

            {/* Cookie Preferences */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Cookie className="w-4 h-4 text-muted-foreground" />
                <Label className="font-medium">Préférences cookies</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                EasyFinder utilise uniquement des cookies essentiels (authentification, préférences). 
                Aucun cookie publicitaire.
              </p>
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Cookies essentiels</p>
                  <p className="text-xs text-muted-foreground">
                    {cookieConsent === "accepted" ? "Acceptés" : cookieConsent === "declined" ? "Refusés" : "Non défini"}
                  </p>
                </div>
                <Switch 
                  checked={cookieConsent === "accepted"} 
                  onCheckedChange={(checked) => {
                    const value = checked ? "accepted" : "declined";
                    localStorage.setItem("easyfinder_cookie_consent", value);
                    setCookieConsent(value);
                    toast.success(checked ? "Cookies acceptés" : "Cookies refusés");
                  }}
                />
              </div>
            </div>

            <Separator />

            {/* Legal Links */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <Label className="font-medium">Informations légales</Label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/privacy")}>
                  Politique de confidentialité
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/terms")}>
                  Conditions d'utilisation
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/legal")}>
                  Mentions légales
                </Button>
              </div>
            </div>

            <Separator />

            {/* Change Google Account */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Changer de compte Google</p>
                <p className="text-sm text-muted-foreground">
                  Se connecter avec un autre compte Google
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate("/auth?select_account=true");
                }}
              >
                Changer
              </Button>
            </div>

            <Separator />

            {/* Logout */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Déconnexion</p>
                <p className="text-sm text-muted-foreground">
                  Se déconnecter de votre compte
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate("/");
                }}
              >
                Se déconnecter
              </Button>
            </div>

            <Separator />

            {/* Delete Account - GDPR/CASA Compliance */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                <Label className="font-medium text-destructive">Zone de danger</Label>
              </div>
              
              <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="font-medium text-destructive">Supprimer mon compte</p>
                    <p className="text-sm text-muted-foreground">
                      Cette action est irréversible. Toutes vos données seront définitivement supprimées.
                    </p>
                  </div>
                  
                  <DeleteAccountDialog userId={user?.id} onDeleted={() => navigate("/")} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
          </main>
        </div>
      </div>
    </div>
  );
};

// Delete Account Dialog Component
const DeleteAccountDialog = ({ 
  userId, 
  onDeleted 
}: { 
  userId: string | undefined; 
  onDeleted: () => void;
}) => {
  const [step, setStep] = useState<"initial" | "confirm">("initial");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    if (!userId || confirmText !== "SUPPRIMER") return;
    
    setDeleting(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Session expirée. Veuillez vous reconnecter.");
        return;
      }

      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { confirmation: "DELETE_MY_ACCOUNT" },
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast.success("Votre compte a été supprimé avec succès.");
      
      // Sign out and redirect
      await supabase.auth.signOut();
      onDeleted();
    } catch (err: any) {
      console.error("Delete account error:", err);
      toast.error(err?.message || "Erreur lors de la suppression du compte");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setStep("initial");
      setConfirmText("");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm">
          <Trash2 className="w-4 h-4 mr-2" />
          Supprimer mon compte
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        {step === "initial" ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Supprimer votre compte ?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  Cette action est <strong>définitive et irréversible</strong>. 
                  Les éléments suivants seront supprimés :
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li>Tous vos documents et fichiers</li>
                  <li>Vos métadonnées et analyses IA</li>
                  <li>Vos connexions Gmail/Dropbox</li>
                  <li>Votre historique et préférences</li>
                  <li>Votre abonnement (sans remboursement)</li>
                </ul>
                <p className="font-medium text-foreground">
                  Êtes-vous sûr de vouloir continuer ?
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <Button 
                variant="destructive" 
                onClick={() => setStep("confirm")}
              >
                Oui, supprimer mon compte
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Confirmation finale
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>
                  Pour confirmer la suppression définitive de votre compte, 
                  tapez <strong className="text-foreground">SUPPRIMER</strong> ci-dessous :
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="Tapez SUPPRIMER"
                  className="font-mono"
                  disabled={deleting}
                />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={confirmText !== "SUPPRIMER" || deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression en cours...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default Settings;
